import client from './client';
import type {
  ValidateResponse,
  LoginCredentialsResponse,
  RegisterLoginResponse,
  ChangePasswordResponse,
} from '../types/api';

export async function validateLicense(
  licenseKey: string,
  deviceId: string,
): Promise<ValidateResponse> {
  const response = await client.post<ValidateResponse>('/api/licenses/validate', {
    licenseKey,
    deviceId,
  });
  return response.data;
}

export async function loginWithCredentials(
  loginId: string,
  password: string,
): Promise<LoginCredentialsResponse> {
  const response = await client.post<LoginCredentialsResponse>('/api/licenses/login', {
    loginId,
    password,
  });
  return response.data;
}

export async function registerLogin(
  licenseKey: string,
  loginId: string,
  password: string,
): Promise<RegisterLoginResponse> {
  const response = await client.post<RegisterLoginResponse>('/api/licenses/register-login', {
    licenseKey,
    loginId,
    password,
  });
  return response.data;
}

export async function changePassword(
  licenseKey: string,
  oldPassword: string,
  newPassword: string,
): Promise<ChangePasswordResponse> {
  const response = await client.post<ChangePasswordResponse>('/api/licenses/change-password', {
    licenseKey,
    oldPassword,
    newPassword,
  });
  return response.data;
}

export interface LicensePaymentStatus {
  paymentStatus: 'PENDING_PAYMENT' | 'PAID' | 'LOCKED' | 'WAIVED' | 'REFUNDED';
  paymentMethod: 'BANK_TRANSFER' | 'WAIVED' | 'LEGACY';
  paymentDueAt: string | null;
  remainingMs: number;
  isLocked: boolean;
  isPaid: boolean;
  isPending: boolean;
  paymentInfo: {
    bankName: string;
    accountNumber: string;
    accountHolder: string;
    amount: number;
    depositorNameHint: string;
  };
}

export async function getMyPaymentStatus(licenseId: string): Promise<LicensePaymentStatus> {
  const response = await client.get<LicensePaymentStatus>(
    `/api/licenses/${licenseId}/payment-status`,
  );
  return response.data;
}

// ─── 매장 추가 관리자 (OWNER 전용 + 본인 비번변경) ───

export interface LicenseMemberView {
  id: string;
  slot: number;
  loginId: string;
  memberName: string;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export async function listLicenseMembers(
  licenseId: string,
  licenseKey: string,
): Promise<LicenseMemberView[]> {
  const response = await client.get<LicenseMemberView[]>(
    `/api/licenses/${licenseId}/members`,
    { params: { licenseKey } },
  );
  return response.data;
}

export async function addLicenseMember(
  licenseId: string,
  licenseKey: string,
  memberName: string,
  password: string,
): Promise<LicenseMemberView> {
  const response = await client.post<LicenseMemberView>(
    `/api/licenses/${licenseId}/members`,
    { licenseKey, memberName, password },
  );
  return response.data;
}

export async function deleteLicenseMember(
  licenseId: string,
  memberId: string,
  licenseKey: string,
): Promise<{ success: true }> {
  const response = await client.delete<{ success: true }>(
    `/api/licenses/${licenseId}/members/${memberId}`,
    { params: { licenseKey } },
  );
  return response.data;
}

export async function resetMemberPassword(
  licenseId: string,
  memberId: string,
  licenseKey: string,
  newPassword: string,
): Promise<{ success: true }> {
  const response = await client.patch<{ success: true }>(
    `/api/licenses/${licenseId}/members/${memberId}/reset-password`,
    { licenseKey, newPassword },
  );
  return response.data;
}

export async function changeMemberOwnPassword(
  loginId: string,
  currentPassword: string,
  newPassword: string,
): Promise<{ success: true }> {
  const response = await client.post<{ success: true }>(
    '/api/licenses/member/change-own-password',
    { loginId, currentPassword, newPassword },
  );
  return response.data;
}
